import React, { useState, useEffect } from "react";
import { Field, ErrorMessage } from "formik";
import apiClient from "../../api/apiClient";

import { useMembershipRegistrationContext } from "../../context/MembershipRegistrationContext";
import PhoneNumberFieldComponent from "../common/PhoneNumberFieldComponent";

export default function ContactDetailsComponent({ formik }) {
  const { countries, provinces } = useMembershipRegistrationContext();
  const [districts, setDistricts] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingDistricts, setLoadingDistricts] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);

  // Fetch districts when province changes
  useEffect(() => {
    const fetchDistricts = async () => {
      if (!formik.values.province) {
        setDistricts([]);
        setCities([]);
        formik.setFieldValue("district", "");
        formik.setFieldValue("city", "");
        return;
      }

      setLoadingDistricts(true);
      try {
        const requestBody = {
          requestHeader: {
            requestId: `REQ-${Date.now()}`,
            requestTimestamp: new Date().toISOString(),
          },
          requestData: {
            provinceId: formik.values.province,
          },
        };

        const response = await apiClient.post(
          "/v1/metadata/get-districts",
          requestBody,
        );
        setDistricts(response.data.responseData.districts || []);
        setCities([]);
        formik.setFieldValue("district", "");
        formik.setFieldValue("city", "");
      } catch (err) {
        setDistricts([]);
      } finally {
        setLoadingDistricts(false);
      }
    };

    fetchDistricts();
    // eslint-disable-next-line
  }, [formik.values.province]);

  // Fetch cities when district changes
  useEffect(() => {
    const fetchCities = async () => {
      if (!formik.values.district) {
        setCities([]);
        formik.setFieldValue("city", "");
        return;
      }

      setLoadingCities(true);
      try {
        const requestBody = {
          requestHeader: {
            requestId: `REQ-${Date.now()}`,
            requestTimestamp: new Date().toISOString(),
          },
          requestData: {
            districtId: formik.values.district,
          },
        };

        const response = await apiClient.post(
          "/v1/metadata/get-cities",
          requestBody,
        );
        setCities(response.data.responseData.cities || []);
        formik.setFieldValue("city", "");
      } catch (err) {
        setCities([]);
      } finally {
        setLoadingCities(false);
      }
    };

    fetchCities();
    // eslint-disable-next-line
  }, [formik.values.district]);

  return (
    <div
      className="col-12 my-0 rounded-3 p-4"
      style={{
        border: "1px solid #e9ecef",
        backgroundColor: "#fff",
      }}
    >
      <h4 className="fw-bold mb-4">Contact Details</h4>

      {/* Email */}
      <div className="col-12 mb-3">
        <label className="form-label">
          Email<span className="text-danger">*</span>
        </label>
        <Field
          type="email"
          name="email"
          placeholder="name@example.com"
          className="form-control"
        />
        <ErrorMessage name="email" component="div" className="text-danger" />
      </div>

      {/* Mobile Number */}
      <PhoneNumberFieldComponent
        label="Mobile Number"
        countryCodeName="mobileCountryCode"
        numberName="mobileNumber"
        placeholder="7XXXXXXXX"
        required={true}
        countries={countries}
      />

      {/* Fixed Line Number */}
      <PhoneNumberFieldComponent
        label="Fixed Line Number"
        countryCodeName="fixedLineCountryCode"
        numberName="fixedLineNumber"
        placeholder="1XXXXXXXX"
        required={false}
        countries={countries}
      />

      {/* WhatsApp Number */}
      <PhoneNumberFieldComponent
        label="WhatsApp Number"
        countryCodeName="whatsappCountryCode"
        numberName="whatsappNumber"
        placeholder="7XXXXXXXX"
        required={true}
        countries={countries}
      />

      {/* Address */}
      <div className="col-12 mb-3">
        <label className="form-label">
          Address<span className="text-danger">*</span>
        </label>
        <Field as="textarea" name="address" className="form-control" rows={2} />
        <ErrorMessage name="address" component="div" className="text-danger" />
      </div>

      {/* Province & District */}
      <div className="row">
        <div className="col-md-6 mb-3">
          <label className="form-label">
            Province<span className="text-danger">*</span>
          </label>
          <Field as="select" name="province" className="form-select">
            <option value="">Select Province</option>
            {provinces.map((province) => (
              <option key={province.id} value={province.id}>
                {province.name}
              </option>
            ))}
          </Field>
          <ErrorMessage
            name="province"
            component="div"
            className="text-danger"
          />
        </div>
        <div className="col-md-6 mb-3">
          <label className="form-label">
            District<span className="text-danger">*</span>
          </label>
          <Field
            as="select"
            name="district"
            className="form-select"
            disabled={!formik.values.province || loadingDistricts}
          >
            <option value="">
              {loadingDistricts ? "Loading..." : "Select District"}
            </option>
            {districts.map((district) => (
              <option key={district.id} value={district.id}>
                {district.name}
              </option>
            ))}
          </Field>
          <ErrorMessage
            name="district"
            component="div"
            className="text-danger"
          />
        </div>
      </div>

      {/* City */}
      <div className="row">
        <div className="col-md-6 mb-3">
          <label className="form-label">
            City<span className="text-danger">*</span>
          </label>
          <Field
            as="select"
            name="city"
            className="form-select"
            disabled={!formik.values.district || loadingCities}
          >
            <option value="">
              {loadingCities ? "Loading..." : "Select City"}
            </option>
            {cities.map((city) => (
              <option key={city.id} value={city.id}>
                {city.name}
              </option>
            ))}
          </Field>
          <ErrorMessage name="city" component="div" className="text-danger" />
        </div>
      </div>

      {/* Lives Abroad */}
      <div className="col-12 mb-3 form-check">
        <Field
          type="checkbox"
          name="abroad"
          className="form-check-input"
          id="livesAbroad"
        />
        <label className="form-check-label" htmlFor="livesAbroad">
          Are you living abroad?
        </label>
      </div>

      {/* Current Country if Lives Abroad */}
      {formik.values.abroad && (
        <div className="col-md-6 mb-3">
          <label className="form-label">Current Country of Residence</label>
          <Field as="select" name="currentCountry" className="form-select">
            <option value="">Select Country</option>
            {countries.map((country) => (
              <option key={country.id} value={country.id}>
                {country.name}
              </option>
            ))}
          </Field>
          <ErrorMessage
            name="currentCountry"
            component="div"
            className="text-danger"
          />
        </div>
      )}
    </div>
  );
}
